import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, Callout, Icon, SelectOption } from '@factorialco/deprecated-design-system'
import { Tooltip } from '@factorialco/f0-react/dist/experimental'
import { BACKEND_DATE_FORMAT } from 'config/Vars'
import {
  AdditionalCompensationFormFieldsFragment,
  ContractsValueObjectsAmountStrategyTypeEnum,
  PayrollConceptSelectFieldFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import MonthPicker from 'modules/core/components/MonthPicker'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import {
  dateBackendToUI,
  dateUIToBackend,
  i18nDateToString,
  stringToDate,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { PayrollConceptSelectField } from 'modules/compensations/components/PayrollConceptSelectField'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { createSelectOptions, parseDateString } from 'modules/contracts/lib/utils'

import { AmountStrategyInputsFields } from './amountStrategyInputs/amountStrategyInputs'

type AdditionalCompensationFormFieldsProps = {
  additionalCompensation?: AdditionalCompensationFormFieldsFragment
  isEditable?: boolean
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  effectiveOn?: string
  isFirstPaymentOnCurrentMonth?: boolean
}

function AdditionalCompensationFormFields({
  additionalCompensation,
  isEditable = true,
  payrollConcepts,
  effectiveOn,
  isFirstPaymentOnCurrentMonth = false,
}: AdditionalCompensationFormFieldsProps) {
  const additionalCompensationForm = useFormContext<AdditionalCompensationFormFieldsFragment>()
  const { control, getValues, setValue, trigger, watch } = additionalCompensationForm

  React.useEffect(() => {
    trigger('firstPaymentOn')
  }, [effectiveOn, trigger])

  const payrollConceptId = watch('payrollConceptId')
  const amountStrategyTypeWatch = watch('amountStrategyType')
  const payrollConceptsOptions = useCategorizedPayrollConcepts(payrollConcepts)
  const firstPaymentOn = isFirstPaymentOnCurrentMonth
    ? moment().startOf('month').format(BACKEND_DATE_FORMAT)
    : undefined

  const selectedPayrollConcept = payrollConceptsOptions.find(
    (pco) => pco.value === payrollConceptId
  )
  const selectedPayrollConceptName = selectedPayrollConcept?.label ?? ''
  const selectedPayrollConceptUnitType =
    payrollConcepts !== undefined &&
    payrollConcepts?.find((pc) => pc.id === payrollConceptId)?.unitType

  React.useEffect(() => {
    setValue('payrollConcept.label', selectedPayrollConceptName)
    if (selectedPayrollConceptUnitType) {
      setValue('payrollConcept.unitType', selectedPayrollConceptUnitType)
    }
  }, [selectedPayrollConceptName, selectedPayrollConceptUnitType, setValue])

  return (
    <Box gap="s16">
      <PayrollConceptSelectField<AdditionalCompensationFormFieldsFragment>
        name="payrollConceptId"
        payrollConcepts={payrollConceptsOptions}
        label={i18n.t(
          'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.label'
        )}
        placeholder={i18n.t(
          'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.placeholder'
        )}
        blankLabel={i18n.t(
          'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.blankLabel'
        )}
        isDisabled={!isEditable}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextArea
            {...field}
            label={i18n.t(
              'contract_versions.additional_compensation.additional_compensation_form_fields.description.label'
            )}
            readonly={!isEditable}
            value={getValues('description') ?? ''}
            onChange={(value) => {
              setValue('description', value, { shouldDirty: true })
              trigger('description')
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="amountStrategyType"
        rules={{ required: true }}
        render={({ field, formState: { errors } }) => {
          const amountStrategyTypeOptions =
            createSelectOptions<ContractsValueObjectsAmountStrategyTypeEnum>([
              'contracts_fixed_amount_strategy',
              'contracts_variable_amount_strategy',
            ] as const)
          return (
            <SelectField
              {...field}
              label={i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.amount_strategy_type.label'
              )}
              none={i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.amount_strategy_type.none'
              )}
              options={amountStrategyTypeOptions}
              value={getValues('amountStrategyType')}
              readonly={!isEditable}
              onChange={(value) => {
                const isFixed = (
                  selectedValue: string
                ): selectedValue is 'contracts_fixed_amount_strategy' =>
                  selectedValue === 'contracts_fixed_amount_strategy'
                setValue(
                  'amountStrategyType',
                  isFixed(value)
                    ? 'contracts_fixed_amount_strategy'
                    : 'contracts_variable_amount_strategy',
                  {
                    shouldDirty: true,
                  }
                )
                trigger('amountStrategyType')
              }}
              onBlur={() => trigger('amountStrategyType')}
              onFocus={() => trigger('amountStrategyType')}
              error={
                errors?.amountStrategyType?.message
                  ? String(errors.amountStrategyType.message)
                  : undefined
              }
              mandatory
              secondaryLabel={
                <Tooltip
                  description={
                    `${i18n.t(
                      'contract_versions.additional_compensation.additional_compensation_form_fields.amounts_strategy_type_information_tooltip_1'
                    )}` +
                    `${i18n.t(
                      'contract_versions.additional_compensation.additional_compensation_form_fields.amounts_strategy_type_information_tooltip_2'
                    )}`
                  }
                >
                  <span>
                    <Icon.Small icon="info" encircled="grey800" />
                  </span>
                </Tooltip>
              }
            />
          )
        }}
      />
      <AmountStrategyInputsFields
        amountStrategyType={amountStrategyTypeWatch ?? additionalCompensation?.amountStrategyType}
        readonly={!isEditable}
        missingLabel={i18n.t('common.missing_field')}
        selectValue={i18n.t('common.select')}
      />
      <Controller
        control={control}
        name="recurrence"
        defaultValue={'monthly'}
        render={({ field, formState: { errors } }) => {
          const recurrenceOptions: SelectOption[] = [
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.monthly_option'
              ),
              value: 'monthly',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_2_months_option'
              ),
              value: 'every_2_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_3_months_option'
              ),
              value: 'every_3_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_4_months_option'
              ),
              value: 'every_4_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_5_months_option'
              ),
              value: 'every_5_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_6_months_option'
              ),
              value: 'every_6_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_7_months_option'
              ),
              value: 'every_7_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_8_months_option'
              ),
              value: 'every_8_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_9_months_option'
              ),
              value: 'every_9_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_10_months_option'
              ),
              value: 'every_10_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_11_months_option'
              ),
              value: 'every_11_months',
            },
            {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_12_months_option'
              ),
              value: 'every_12_months',
            },
          ]
          return (
            <SelectField
              {...field}
              label={i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.label'
              )}
              readonly={!isEditable}
              options={recurrenceOptions}
              value={getValues('recurrence') ?? 'monthly'}
              onChange={(value) => {
                setValue('recurrence', value)
              }}
              onBlur={() => trigger('recurrence')}
              onFocus={() => trigger('recurrence')}
              error={errors?.recurrence?.message ? String(errors.recurrence.message) : undefined}
            />
          )
        }}
      />
      <Controller
        control={control}
        name="firstPaymentOn"
        defaultValue={firstPaymentOn}
        rules={{
          validate: (value) => {
            if (!value) return true

            const firstPaymentValue = parseDateString(value)

            if (!firstPaymentValue) return true

            if (effectiveOn || firstPaymentOn) {
              const effectiveDate = effectiveOn ? parseDateString(effectiveOn) : null
              const firstPaymentDate = firstPaymentOn ? parseDateString(firstPaymentOn) : null
              const date = effectiveDate ?? firstPaymentDate

              if (!date) return true

              const minDate = date.startOf('month')

              if (firstPaymentValue.isBefore(minDate, 'month')) {
                if (effectiveOn && effectiveDate) {
                  return i18n.t(
                    'contract_versions.additional_compensation.errors.first_payment_on_before_contract_effective_month',
                    {
                      contract_effective_date: i18nDateToString(effectiveDate, 'ui'),
                    }
                  )
                }
                if (firstPaymentOn) {
                  return i18n.t(
                    'contract_versions.additional_compensation.errors.first_payment_on_before_current_month'
                  )
                }
              }
            }

            return true
          },
        }}
        render={({ field, formState: { errors } }) => {
          const fieldValue = field.value ?? undefined
          const backendValue = fieldValue ?? firstPaymentOn
          const formattedDate = backendValue ? dateBackendToUI(backendValue) : undefined

          const firstPaymentValueDate = backendValue
            ? stringToDate(String(backendValue), 'backend')
            : null
          const effectiveDate = effectiveOn ? moment(effectiveOn) : null
          const firstPaymentDate = firstPaymentOn
            ? stringToDate(String(firstPaymentOn), 'backend')
            : null

          const isNotFirstDay = effectiveDate?.date() !== 1
          const isSameMonth = (firstPayment: moment.Moment | null, date: moment.Moment | null) =>
            !!firstPayment && !!date && firstPayment.isSame(date, 'month')

          const showEffectiveDateAlert =
            isSameMonth(firstPaymentValueDate, effectiveDate) && isNotFirstDay
          const showFirstPaymentDateAlert = isSameMonth(firstPaymentValueDate, firstPaymentDate)

          return (
            <>
              <MonthPicker
                {...field}
                label={i18n.t(
                  'contract_versions.additional_compensation.additional_compensation_form_fields.firstPaymentOn.label'
                )}
                readonly={!isEditable}
                value={formattedDate}
                onChange={(monthValue) => {
                  field.onChange(dateUIToBackend(monthValue))
                  trigger('firstPaymentOn')
                }}
                error={
                  errors?.firstPaymentOn?.message
                    ? String(errors.firstPaymentOn.message)
                    : undefined
                }
              />
              {showEffectiveDateAlert && (
                <Callout
                  title={i18n.t(
                    'contract_versions.additional_compensation.additional_compensation_form_fields.first_payment_on.first_payment_same_month_as_effective_title'
                  )}
                  description={i18n.t(
                    'contract_versions.additional_compensation.additional_compensation_form_fields.first_payment_on.first_payment_same_month_as_effective_description'
                  )}
                  type="info"
                />
              )}
              {showFirstPaymentDateAlert && (
                <Callout
                  title={i18n.t(
                    'contract_versions.additional_compensation.additional_compensation_form_fields.first_payment_on.same_month_as_current_month_title'
                  )}
                  description={i18n.t(
                    'contract_versions.additional_compensation.additional_compensation_form_fields.first_payment_on.same_month_as_current_month_description'
                  )}
                  type="info"
                />
              )}
            </>
          )
        }}
      />
    </Box>
  )
}

export { AdditionalCompensationFormFields }
